import { useCallback, useEffect, useRef } from 'react';
import type { Mood } from 'shared';
import { audioEngine, type HeartbeatHandle } from '../../../audio/audioEngine';

/**
 * Foundation hook: useMinigameSound（R2 感覚層・2026-06-21）
 *
 * ミニゲーム共通の効果音窓口。mood を束ねて audioEngine.playSfx を呼ぶ薄いラッパ。
 * 後続の Foundation 配線（JudgementModal / FailOverlay / HintBubble / 各テンプレ）が
 * この hook 経由で発音する（テンプレが直接 audioEngine を触らないための境界）。
 *
 * 設計:
 *  - 引数 mood を全 SFX に自動付与する（テンプレ側で mood を毎回渡さなくてよい）。
 *  - heartbeat(bpm?) は HeartbeatHandle を返し、**アンマウントで自動 stop** する。
 *    同じコンポーネントで複数回 heartbeat() を呼んでも、前のハンドルは追跡され掃除される。
 *  - audioEngine は unlock 前・mute 中の呼び出しを安全に無視するので、ここでの発火条件判定は不要。
 */
export interface MinigameSound {
  /** 成功音 */
  success: () => void;
  /** 失敗音 */
  fail: () => void;
  /** タップ/クリック音 */
  tap: () => void;
  /** ヒント提示音 */
  hint: () => void;
  /** 開示/発見音 */
  reveal: () => void;
  /** 選択確定音 */
  select: () => void;
  /** 警告音 */
  warning: () => void;
  /**
   * 鼓動ループを開始しハンドルを返す。bpm 省略時は 60。
   * アンマウント時に自動で stop されるが、明示的に止めたい時は handle.stop() を呼ぶ。
   */
  heartbeat: (bpm?: number) => HeartbeatHandle;
  /**
   * アセット音源（URL）を 1 発鳴らす。`config.audio.successSE/failSE/tapSE` を
   * 「指定があればアセット音・無ければ合成音」にするための土台（R2 Wave2C）。
   * 空 URL / 失敗は無視。master mute/volume に連動する（mute 中は鳴らない）。
   */
  sfxAsset: (url: string, volume?: number) => void;
}

export function useMinigameSound(mood?: Mood): MinigameSound {
  // この hook が起動した heartbeat を全て追跡し、アンマウントでまとめて stop する
  const handlesRef = useRef<Set<HeartbeatHandle>>(new Set());

  useEffect(() => {
    const handles = handlesRef.current;
    return () => {
      for (const h of handles) {
        try {
          h.stop();
        } catch {
          /* 既に停止済みなら無視 */
        }
      }
      handles.clear();
    };
  }, []);

  // mood を ref に保持し、コールバックの参照を安定させる（mood 変化で再生成しない）
  const moodRef = useRef<Mood | undefined>(mood);
  moodRef.current = mood;

  const success = useCallback(() => audioEngine.playSfx('success', moodRef.current ? { mood: moodRef.current } : undefined), []);
  const fail = useCallback(() => audioEngine.playSfx('fail', moodRef.current ? { mood: moodRef.current } : undefined), []);
  const tap = useCallback(() => audioEngine.playSfx('tap', moodRef.current ? { mood: moodRef.current } : undefined), []);
  const hint = useCallback(() => audioEngine.playSfx('hint', moodRef.current ? { mood: moodRef.current } : undefined), []);
  const reveal = useCallback(() => audioEngine.playSfx('reveal', moodRef.current ? { mood: moodRef.current } : undefined), []);
  const select = useCallback(() => audioEngine.playSfx('select', moodRef.current ? { mood: moodRef.current } : undefined), []);
  const warning = useCallback(() => audioEngine.playSfx('warning', moodRef.current ? { mood: moodRef.current } : undefined), []);

  const heartbeat = useCallback((bpm?: number): HeartbeatHandle => {
    const handle = audioEngine.startHeartbeat({ bpm, mood: moodRef.current });
    const handles = handlesRef.current;
    handles.add(handle);
    // stop されたら追跡集合からも外す（メモリリーク防止）
    const originalStop = handle.stop.bind(handle);
    const wrapped: HeartbeatHandle = {
      setBpm: handle.setBpm.bind(handle),
      stop: () => {
        originalStop();
        handles.delete(wrapped);
      },
    };
    // 集合に入れるのは wrapped（delete 整合のため差し替え）
    handles.delete(handle);
    handles.add(wrapped);
    return wrapped;
  }, []);

  // アセット SFX（mood 非依存・URL 直指定）。空 URL/失敗/mute は audioEngine 側で安全に無視。
  const sfxAsset = useCallback(
    (url: string, volume?: number) =>
      audioEngine.playSfxAsset(url, volume != null ? { volume } : undefined),
    [],
  );

  return { success, fail, tap, hint, reveal, select, warning, heartbeat, sfxAsset };
}
